import type { FC, FormEvent, ReactNode } from 'react'
import type { Product } from '@/types'

import {
    useEffect,
    useId,
    useRef,
    useState
} from 'react'
import { Check, X } from 'lucide-react'
import { gsap, useGSAP } from '@/lib/gsap'
import { isEmail } from '@/lib/format'
import { prefersReducedMotion } from '@/lib/motion'
import { cn } from '@/lib/utils'
import { getLenis } from '@/components/motion/smooth-scroll'
import Button from '@/components/generals/button'
import Img from '@/components/generals/img'

interface RequestDrawerProps {
    product: Product
    size: string | null
    open: boolean
    onClose: () => void
}

type Status = 'idle' | 'sending' | 'done' | 'error'

const fieldClass = 'h-12 w-full border-b border-ink/25 bg-transparent text-base text-ink outline-none transition-colors duration-300 placeholder:text-ink/40 focus:border-ink'

/**
 * "Buy now" opens this drawer: the visitor leaves their details and the studio
 * replies with a payment link. Requests are saved for the studio to follow up.
 */
const RequestDrawer: FC<RequestDrawerProps> = ({ product, size, open, onClose }): ReactNode => {
    const id = useId()
    const root = useRef<HTMLDivElement>(null)
    const mounted = useRef(false)
    const [status, setStatus] = useState<Status>('idle')
    const [message, setMessage] = useState('')
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [form, setForm] = useState({ name: '', email: '', note: '', company: '' })

    useGSAP(() => {
        const element = root.current

        if (!element) return

        const overlay = element.querySelector('[data-drawer-overlay]')
        const panel = element.querySelector('[data-drawer-panel]')
        const items = element.querySelectorAll('[data-drawer-item]')
        const fast = prefersReducedMotion()

        if (!mounted.current) {
            mounted.current = true
            gsap.set(element, { autoAlpha: 0 })
            gsap.set(panel, { xPercent: 100 })

            return
        }

        if (open) {
            gsap.timeline()
                .set(element, { autoAlpha: 1 })
                .fromTo(overlay, { autoAlpha: 0 }, { autoAlpha: 1, duration: fast ? 0 : 0.5, ease: 'power2.out' }, 0)
                .to(panel, { xPercent: 0, duration: fast ? 0 : 0.9, ease: 'expo.out' }, 0)
                .fromTo(items, { autoAlpha: 0, y: 24 }, { autoAlpha: 1, y: 0, duration: fast ? 0 : 0.8, stagger: 0.05 }, 0.2)
        } else {
            gsap.timeline()
                .to(panel, { xPercent: 100, duration: fast ? 0 : 0.7, ease: 'expo.inOut' }, 0)
                .to(overlay, { autoAlpha: 0, duration: fast ? 0 : 0.5 }, 0.15)
                .set(element, { autoAlpha: 0 })
        }
    }, { scope: root, dependencies: [open], revertOnUpdate: false })

    useEffect(() => {
        if (!open) return

        getLenis()?.stop()

        const onKey = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') onClose()
        }

        window.addEventListener('keydown', onKey)

        const timer = window.setTimeout(() => {
            root.current?.querySelector<HTMLInputElement>('input[name="name"]')?.focus({ preventScroll: true })
        }, 350)

        return () => {
            window.removeEventListener('keydown', onKey)
            window.clearTimeout(timer)

            if (!document.documentElement.classList.contains('is-transitioning')) getLenis()?.start()
        }
    }, [open, onClose])

    useEffect(() => {
        if (open) return

        const timer = window.setTimeout(() => {
            if (status === 'done') {
                setStatus('idle')
                setMessage('')
            }
        }, 800)

        return () => window.clearTimeout(timer)
    }, [open, status])

    const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        const nextErrors: Record<string, string> = {}

        if (!form.name.trim()) nextErrors.name = 'Please tell us your name.'
        if (!isEmail(form.email)) nextErrors.email = 'Please enter a valid email address.'

        setErrors(nextErrors)

        if (Object.keys(nextErrors).length) return

        setStatus('sending')
        setMessage('')

        try {
            const response = await fetch('/api/requests', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ productId: product.id, size, ...form })
            })

            const payload = await response.json().catch(() => null) as { message?: string, errors?: Record<string, string> } | null

            if (!response.ok) {
                if (payload?.errors) setErrors(payload.errors)

                throw new Error(payload?.message || 'Something went wrong.')
            }

            setStatus('done')
            setForm({ name: '', email: '', note: '', company: '' })
        } catch (error) {
            setStatus('error')
            setMessage(error instanceof Error ? error.message : 'Something went wrong. Please try again.')
        }
    }

    return (
        <div
            ref={root}
            role='dialog'
            aria-modal='true'
            aria-labelledby={`${id}-title`}
            aria-hidden={!open}
            className='invisible fixed inset-0 z-[60]'
        >
            <button
                type='button'
                data-drawer-overlay
                aria-label='Close'
                tabIndex={open ? 0 : -1}
                onClick={onClose}
                className='absolute inset-0 cursor-default bg-ink/70 backdrop-blur-sm'
            />

            <div
                data-drawer-panel
                data-lenis-prevent
                className='absolute inset-y-0 right-0 flex w-full max-w-[520px] flex-col overflow-y-auto bg-bone text-ink'
            >
                <div className='flex items-center justify-between border-b border-ink/15 px-6 py-5 md:px-8'>
                    <p className='label-mono'>
                        (Request) — {product.style_code ? <span data-mdlfy-cms={`products:${product.id}:style_code`}>{product.style_code}</span> : 'Elio'}
                    </p>

                    <button
                        type='button'
                        onClick={onClose}
                        tabIndex={open ? 0 : -1}
                        aria-label='Close'
                        className='flex size-10 items-center justify-center border border-ink/20 transition-colors duration-300 hover:bg-ink hover:text-bone'
                    >
                        <X aria-hidden='true' className='size-4' />
                    </button>
                </div>

                <div className='flex flex-1 flex-col px-6 py-8 md:px-8'>
                    <div data-drawer-item className='flex gap-5'>
                        <div className='relative aspect-[3/4] w-24 shrink-0 overflow-hidden bg-ink/10'>
                            <Img src={product.image} alt={product.image_alt || product.name} sizes='96px' />
                        </div>

                        <div className='flex flex-col justify-between py-1'>
                            <div>
                                <h2 id={`${id}-title`} className='font-heading text-3xl uppercase leading-[0.95]'>
                                    <span data-mdlfy-cms={`products:${product.id}:name`}>{product.name}</span>
                                </h2>

                                <p className='mt-2 meta-mono text-ink/60'>
                                    Size {size ?? '—'}
                                </p>
                            </div>

                            <p className='font-mono text-lg'>
                                <span data-mdlfy-cms={`products:${product.id}:price`}>{product.price}</span>
                            </p>
                        </div>
                    </div>

                    {status === 'done' ? (
                        <div data-drawer-item className='mt-10 flex flex-1 flex-col'>
                            <span className='flex size-12 items-center justify-center rounded-full bg-ink text-acid'>
                                <Check aria-hidden='true' className='size-5' />
                            </span>

                            <p className='mt-6 font-heading text-5xl uppercase leading-[0.9]'>
                                Request received
                            </p>

                            <p className='mt-4 max-w-[40ch] body-copy text-ink/70' role='status'>
                                The studio will reply within 24 hours with a secure payment link and shipping details. Your piece is held until then.
                            </p>

                            <div className='mt-auto pt-10'>
                                <Button variant='ink' onClick={onClose} icon={false}>
                                    Keep browsing
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={submit} noValidate className='mt-10 flex flex-1 flex-col'>
                            <p data-drawer-item className='max-w-[42ch] body-copy text-ink/70'>
                                Leave your details and the studio will send a secure payment link within 24 hours. The piece is held for you until then.
                            </p>

                            <div data-drawer-item className='mt-8'>
                                <label htmlFor={`${id}-name`} className='meta-mono text-ink/60'>
                                    Name
                                </label>

                                <input
                                    id={`${id}-name`}
                                    name='name'
                                    autoComplete='name'
                                    value={form.name}
                                    onChange={event => setForm(current => ({ ...current, name: event.target.value }))}
                                    placeholder='Your name'
                                    aria-invalid={Boolean(errors.name)}
                                    className={cn(fieldClass, errors.name && 'border-destructive')}
                                />

                                {errors.name ? <p className='mt-2 text-sm text-[#b3261e]'>{errors.name}</p> : null}
                            </div>

                            <div data-drawer-item className='mt-6'>
                                <label htmlFor={`${id}-email`} className='meta-mono text-ink/60'>
                                    Email
                                </label>

                                <input
                                    id={`${id}-email`}
                                    name='email'
                                    type='email'
                                    autoComplete='email'
                                    value={form.email}
                                    onChange={event => setForm(current => ({ ...current, email: event.target.value }))}
                                    placeholder='you@domain.com'
                                    aria-invalid={Boolean(errors.email)}
                                    className={cn(fieldClass, errors.email && 'border-destructive')}
                                />

                                {errors.email ? <p className='mt-2 text-sm text-[#b3261e]'>{errors.email}</p> : null}
                            </div>

                            <div data-drawer-item className='mt-6'>
                                <label htmlFor={`${id}-note`} className='meta-mono text-ink/60'>
                                    Note (optional)
                                </label>

                                <textarea
                                    id={`${id}-note`}
                                    name='note'
                                    rows={3}
                                    value={form.note}
                                    onChange={event => setForm(current => ({ ...current, note: event.target.value }))}
                                    placeholder='Delivery city, fit questions, a gift note.'
                                    className='w-full resize-none border-b border-ink/25 bg-transparent py-3 text-base text-ink outline-none transition-colors duration-300 placeholder:text-ink/40 focus:border-ink'
                                />
                            </div>

                            <input
                                type='text'
                                name='company'
                                tabIndex={-1}
                                autoComplete='off'
                                aria-hidden='true'
                                value={form.company}
                                onChange={event => setForm(current => ({ ...current, company: event.target.value }))}
                                className='hidden'
                            />

                            {message ? <p role='alert' className='mt-6 text-sm text-[#b3261e]'>{message}</p> : null}

                            <div data-drawer-item className='mt-auto pt-10'>
                                <Button type='submit' variant='ink' className='w-full' disabled={status === 'sending'}>
                                    {status === 'sending' ? 'Sending request' : 'Send request'}
                                </Button>

                                <p className='mt-4 meta-mono text-ink/50'>
                                    Free repairs for life · 30-day returns
                                </p>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    )
}

export default RequestDrawer
